import { useSessionKey } from "@auth/sessionStore";
import { useUser } from "@context/UserContext";
import {
  faCircleCheck,
  faMicrophone,
  faStop,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  ConfirmDialog,
  FieldError,
  SettingsCard,
  StatusMark,
  Swipe,
} from "@scribenote/brian-design-system";
import { Navigate } from "@tanstack/react-router";
import { formatDateTime } from "@utils/format-date";
import { useEnrollmentInterview } from "@voiceprint/useEnrollmentInterview";
import {
  useDeleteVoiceprint,
  useVoiceprintStatus,
} from "@voiceprint/voiceprintQueries";
import { useState } from "react";

export const VoiceprintPage = () => {
  const sessionKey = useSessionKey();
  return <EnrollmentPage key={sessionKey} />;
};

const EnrollmentPage = () => {
  const { session } = useUser();
  const sessionKey = useSessionKey();
  const interview = useEnrollmentInterview();
  const statusQuery = useVoiceprintStatus(
    sessionKey,
    interview.status === "uploading",
  );
  const deleteVoiceprint = useDeleteVoiceprint();
  const [consent, setConsent] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  if (!session) return <Navigate to="/" />;
  const status = statusQuery.data;
  const live =
    interview.status === "listening" || interview.status === "finishing";
  const busy =
    live ||
    interview.status === "connecting" ||
    interview.status === "uploading";
  const color =
    interview.speechSeconds >= 45
      ? "bg-enrollment-ready"
      : interview.speechSeconds >= 30
        ? "bg-enrollment-building"
        : interview.speechSeconds >= 15
          ? "bg-enrollment-starting"
          : "bg-enrollment-short";
  const quality =
    interview.speechSeconds >= 45 ? "Longer sample" : "Building your sample";
  const hasStoredData =
    !!status &&
    (status.enrolled || status.sampleCount > 0 || status.pendingCount > 0);

  return (
    <>
      <section
        aria-label="Voiceprint enrollment"
        className="flex min-w-0 flex-1 flex-col"
      >
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 rounded-[4px] border border-rule bg-card px-9 pt-7 pb-8 shadow-sheet">
          <SettingsCard
            title="Teach Brian your voice"
            subtitle="Have a short conversation with Brian about something you know well. Speak naturally—you don’t need to get every word right. You can stop at any time; more speech gives Brian more to work with."
          >
            <div className="flex flex-col gap-4">
              <p className="text-sm text-annotation">
                Use the best quality microphone available, ideally close to your
                mouth. Headphones and a quiet spot help keep Brian’s voice out
                of your recording and away from nearby room microphones.
              </p>
              {statusQuery.isLoading && (
                <p className="text-sm text-annotation">Checking…</p>
              )}
              {statusQuery.isError && (
                <FieldError>Couldn't check your voiceprint status.</FieldError>
              )}
              {status && (
                <div className="flex flex-wrap items-center gap-3">
                  {status.enrolled ? (
                    <Swipe
                      tone="working"
                      icon={
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          aria-hidden="true"
                        />
                      }
                    >
                      Enrolled
                    </Swipe>
                  ) : (
                    <StatusMark>Not enrolled</StatusMark>
                  )}
                  {status.enrolled && status.updatedAt && (
                    <span className="text-fine-print text-annotation">
                      Last updated {formatDateTime(status.updatedAt)}
                    </span>
                  )}
                  {hasStoredData && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => setConfirmingDelete(true)}
                    >
                      <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                      Delete my voiceprint
                    </Button>
                  )}
                </div>
              )}
              {!busy && interview.status !== "ready" && (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                  />
                  <span>
                    I agree to Brian processing my voice and storing a biometric
                    voice signature to identify me until I delete it.
                  </span>
                </label>
              )}
              <p className="text-sm text-annotation">
                This conversation is only for enrollment. It cannot access
                clinic records or make changes. Talk about your general routine
                rather than real patients or clients.
              </p>
              {(live || interview.status === "connecting") && (
                <div
                  className="flex items-center justify-between gap-3"
                  aria-live="polite"
                >
                  <span className="text-sm text-annotation">
                    {interview.status === "connecting"
                      ? "Connecting to Brian…"
                      : interview.status === "finishing"
                        ? "Wrapping up…"
                        : interview.speaking
                          ? "Brian is speaking…"
                          : "Brian is listening…"}
                  </span>
                  <span className="flex items-center gap-2 text-sm">
                    <span
                      role="img"
                      aria-label={quality}
                      className={`size-3 shrink-0 rounded-full ${color}`}
                    />
                    <span className="font-mono tabular-nums">
                      {Math.floor(interview.speechSeconds / 60)}:
                      {String(interview.speechSeconds % 60).padStart(2, "0")}{" "}
                      speaking
                    </span>
                  </span>
                </div>
              )}
              {interview.turns.length > 0 && (
                <div
                  className="max-h-80 overflow-y-auto rounded border border-rule/50 p-4"
                  data-dd-privacy="hidden"
                  aria-label="Enrollment conversation"
                >
                  {interview.turns.map((turn, index) => (
                    <p key={index} className="mb-3 text-sm last:mb-0">
                      <span className="font-semibold">{turn.role}: </span>
                      {turn.text}
                    </p>
                  ))}
                </div>
              )}
              {interview.error && <FieldError>{interview.error}</FieldError>}
              {interview.status === "uploading" && (
                <p role="status" className="text-sm text-annotation">
                  Building your voice signature…
                </p>
              )}
              {interview.status === "ready" && (
                <p role="status" className="text-sm text-annotation">
                  Your voice signature is ready.
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {!busy && interview.status !== "ready" && (
                  <Button
                    variant="accent"
                    disabled={!consent || !status}
                    onClick={() => void interview.start()}
                    data-testid="voiceprint-start"
                  >
                    <FontAwesomeIcon icon={faMicrophone} aria-hidden="true" />
                    {interview.status === "error"
                      ? "Start again"
                      : "Start enrollment"}
                  </Button>
                )}
                {interview.status === "listening" && (
                  <Button variant="accent" onClick={interview.stop}>
                    <FontAwesomeIcon icon={faStop} aria-hidden="true" />
                    Finish
                  </Button>
                )}
                {interview.status === "error" && interview.canRetry && (
                  <Button
                    variant="outline"
                    onClick={() => void interview.retry()}
                  >
                    Retry saving
                  </Button>
                )}
                {interview.status !== "idle" &&
                  interview.status !== "uploading" && (
                    <Button variant="ghost" onClick={interview.cancel}>
                      {interview.status === "ready" ? "Done" : "Cancel"}
                    </Button>
                  )}
              </div>
            </div>
          </SettingsCard>
        </div>
      </section>
      <ConfirmDialog
        open={confirmingDelete}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={() => {
          interview.cancel();
          setConsent(false);
          deleteVoiceprint.mutate(undefined, {
            onSuccess: () => setConfirmingDelete(false),
          });
        }}
        title="Delete your voiceprint?"
        destructive
        busy={deleteVoiceprint.isPending}
        confirmLabel="Delete"
      >
        <p>
          This removes your stored voice signature and enrollment records. Brian
          will stop using your voice to identify you until you enroll again.
        </p>
      </ConfirmDialog>
    </>
  );
};
